import React from 'react';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Badge,
  Chip
} from '@mui/material';
import {
  Circle as CircleIcon,
  Business as BusinessIcon,
  Person as PersonIcon
} from '@mui/icons-material';
// Fonction simple pour formater le temps
const formatTimeAgo = (timestamp) => {
  const now = new Date();
  const messageTime = new Date(timestamp);
  const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'À l\'instant';
  if (diffInMinutes < 60) return `${diffInMinutes}min`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}j`;
  
  return messageTime.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};

const ConversationList = ({ 
  conversations, 
  activeConversation, 
  onConversationSelect, 
  currentUser 
}) => {
  const getOtherParticipant = (conversation) => {
    return conversation.participants.find(p => p.id !== currentUser.id);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return formatTimeAgo(timestamp);
    } catch (error) {
      return 'Il y a quelques instants';
    }
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <List className="conversation-list">
      {conversations.length === 0 ? (
        <Box className="no-conversations">
          <Typography variant="body2" color="textSecondary">
            Aucune conversation trouvée
          </Typography>
        </Box>
      ) : (
        conversations.map((conversation) => {
          const otherParticipant = getOtherParticipant(conversation);
          const isActive = activeConversation?.id === conversation.id;
          const hasUnread = conversation.unreadCount > 0;

          return (
            <ListItem
              key={conversation.id}
              button
              onClick={() => onConversationSelect(conversation)}
              className={`conversation-item ${
                isActive ? 'conversation-item-active' : ''
              } ${
                hasUnread ? 'conversation-item-unread' : ''
              }`}
              divider
            >
              <ListItemAvatar>
                <Box className="avatar-container">
                  <Avatar
                    src={otherParticipant?.avatar}
                    alt={otherParticipant?.name}
                    className="conversation-avatar"
                  >
                    {otherParticipant?.type === 'organization' ? (
                      <BusinessIcon />
                    ) : (
                      <PersonIcon />
                    )}
                  </Avatar>
                  
                  {/* Indicateur de statut en ligne */}
                  <Box 
                    className={`online-status ${
                      otherParticipant?.isOnline ? 'online' : 'offline'
                    }`}
                  >
                    <CircleIcon className="status-icon" />
                  </Box>
                </Box>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box className="conversation-header">
                    <Typography 
                      variant="subtitle1" 
                      className={`participant-name ${
                        hasUnread ? 'participant-name-unread' : ''
                      }`}
                    >
                      {otherParticipant?.name}
                    </Typography>
                    
                    <Box className="conversation-meta">
                      {/* Badge type d'utilisateur */}
                      <Chip
                        label={otherParticipant?.type === 'organization' ? 'Org' : 'Pro'}
                        size="small"
                        className={`user-type-chip ${
                          otherParticipant?.type === 'organization' 
                            ? 'organization-chip' 
                            : 'professional-chip'
                        }`}
                      />
                      
                      {/* Timestamp */}
                      <Typography 
                        variant="caption" 
                        className="conversation-timestamp"
                      >
                        {formatTimestamp(conversation.lastMessage.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                }
                secondary={
                  <Box className="last-message-container">
                    <Typography 
                      variant="body2" 
                      className={`last-message ${
                        hasUnread ? 'last-message-unread' : ''
                      }`}
                    >
                      {conversation.lastMessage.senderId === currentUser.id && (
                        <span className="message-sender">Vous: </span>
                      )}
                      {truncateMessage(conversation.lastMessage.content)}
                    </Typography>
                    
                    {/* Badge messages non lus */}
                    {hasUnread && (
                      <Badge 
                        badgeContent={conversation.unreadCount} 
                        color="primary"
                        className="unread-count-badge"
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          );
        })
      )}
    </List>
  );
};

export default ConversationList;